import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Rocket, HelpCircle, Menu, X } from 'lucide-react';
import QRModal from './QRModal';
import { useAuth } from '../context/AuthContext';
import { Navbar, Logo, LogoImage, NavLinks, NavLink, BetaButton } from '../styles/layout';
import {
  NavRight,
  SignInButton,
  NavDropdownWrapper,
  NavDropdownToggle,
  MegaMenuPanel,
  MegaMenuInner,
  MegaMenuColumn,
  MegaMenuColumnTitle,
  MegaMenuItem,
  MegaMenuIcon,
  MegaMenuItemText,
  MegaMenuItemTitle,
  MegaMenuItemDesc,
  HamburgerButton,
  MobileMenuOverlay,
  MobileMenuPanel,
  MobileMenuLink,
} from '../styles/nav';

const resourcesCategories = [
  {
    title: 'Learn',
    items: [
      {
        to: '/resources/getting-started',
        icon: Rocket,
        title: 'Getting Started',
        description: 'Set your team up in minutes.',
      },
      {
        to: '/resources/faq',
        icon: HelpCircle,
        title: 'FAQ',
        description: 'Answers to common questions.',
      },
    ],
  },
];

function Nav() {
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileResourcesOpen(false);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      <Navbar initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.5 }}>
        {/* Logo always links home */}
        <Logo as={Link} to="/" style={{ cursor: 'pointer' }}>
          <LogoImage src="/PlayfairLogo.png" alt="Playfair Logo" />
        </Logo>

        <NavLinks>
          <NavLink as={Link} to="/about">
            About Us
          </NavLink>
          <NavLink as={Link} to="/who-uses-playfair">
            Who Uses Playfair
          </NavLink>

          {currentUser && (
            <NavLink as={Link} to="/perks">
              Perks
            </NavLink>
          )}

          <NavDropdownWrapper
            onMouseEnter={() => setIsResourcesOpen(true)}
            onMouseLeave={() => setIsResourcesOpen(false)}
          >
            <NavDropdownToggle
              type="button"
              onClick={() => setIsResourcesOpen((prev) => !prev)}
            >
              Resources <ChevronDown size={14} />
            </NavDropdownToggle>

            <AnimatePresence>
              {isResourcesOpen && (
                <MegaMenuPanel
                  initial={{ clipPath: 'inset(0% 0% 100% 0%)', opacity: 0 }}
                  animate={{ clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 }}
                  exit={{ clipPath: 'inset(0% 0% 100% 0%)', opacity: 0 }}
                  transition={{ duration: 0.35, ease: 'easeInOut' }}
                >
                  <MegaMenuInner>
                    {resourcesCategories.map((category) => (
                      <MegaMenuColumn key={category.title}>
                        <MegaMenuColumnTitle>{category.title}</MegaMenuColumnTitle>
                        {category.items.map(({ to, icon: Icon, title, description }) => (
                          <MegaMenuItem
                            key={to}
                            to={to}
                            onClick={() => setIsResourcesOpen(false)}
                          >
                            <MegaMenuIcon>
                              <Icon size={18} />
                            </MegaMenuIcon>
                            <MegaMenuItemText>
                              <MegaMenuItemTitle>{title}</MegaMenuItemTitle>
                              <MegaMenuItemDesc>{description}</MegaMenuItemDesc>
                            </MegaMenuItemText>
                          </MegaMenuItem>
                        ))}
                      </MegaMenuColumn>
                    ))}
                  </MegaMenuInner>
                </MegaMenuPanel>
              )}
            </AnimatePresence>
          </NavDropdownWrapper>

          <NavLink as={Link} to="/contact">
            Contact
          </NavLink>
        </NavLinks>

        <NavRight>
          {currentUser ? (
            <SignInButton type="button" onClick={handleSignOut}>
              Sign Out
            </SignInButton>
          ) : (
            <SignInButton as={Link} to="/sign-in">
              Sign In
            </SignInButton>
          )}

          <BetaButton onClick={() => setIsQRModalOpen(true)}>Download, it's free</BetaButton>

          <HamburgerButton
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} color="#fff" /> : <Menu size={24} color="#fff" />}
          </HamburgerButton>
        </NavRight>
      </Navbar>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobileMenu}
            />
            <MobileMenuPanel
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <MobileMenuLink to="/about" onClick={closeMobileMenu}>
                About Us
              </MobileMenuLink>
              <MobileMenuLink to="/who-uses-playfair" onClick={closeMobileMenu}>
                Who Uses Playfair
              </MobileMenuLink>

              {currentUser && (
                <MobileMenuLink to="/perks" onClick={closeMobileMenu}>
                  Perks
                </MobileMenuLink>
              )}

              <NavDropdownToggle
                type="button"
                onClick={() => setIsMobileResourcesOpen((prev) => !prev)}
                style={{ color: '#fff' }}
              >
                Resources <ChevronDown size={14} />
              </NavDropdownToggle>

              {isMobileResourcesOpen &&
                resourcesCategories.map((category) =>
                  category.items.map(({ to, title }) => (
                    <MobileMenuLink
                      key={to}
                      to={to}
                      onClick={closeMobileMenu}
                      style={{ paddingLeft: '1rem', fontSize: '1rem', fontWeight: 400 }}
                    >
                      {title}
                    </MobileMenuLink>
                  ))
                )}

              <MobileMenuLink to="/contact" onClick={closeMobileMenu}>
                Contact
              </MobileMenuLink>

              {currentUser ? (
                <MobileMenuLink
                  as="button"
                  onClick={() => {
                    closeMobileMenu();
                    handleSignOut();
                  }}
                >
                  Sign Out
                </MobileMenuLink>
              ) : (
                <MobileMenuLink to="/sign-in" onClick={closeMobileMenu}>
                  Sign In
                </MobileMenuLink>
              )}

              <BetaButton
                onClick={() => {
                  closeMobileMenu();
                  setIsQRModalOpen(true);
                }}
                style={{ marginTop: '1rem', width: '100%' }}
              >
                Download, it's free
              </BetaButton>
            </MobileMenuPanel>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        <QRModal isOpen={isQRModalOpen} onClose={() => setIsQRModalOpen(false)} />
      </AnimatePresence>
    </>
  );
}

export default Nav;
